// Common
import { type HttpContext } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';

// Types
import { mailboxTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { Application } from '@modules/common/types/application';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { Message } from '@modules/messages/types/message';
import { MessageFolder } from '@modules/messages/types/message-folder';
import { MessageFoldersFilters } from '@modules/messages/types/message-folders-filters';
import { MessagesCounts } from '@modules/messages/types/messages-counts';
import { VirtualFolder } from '@modules/messages/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';

// Services
import { MessageFolderListStateService } from '@modules/messages/services/message-folder-list-state.service';

// RX
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Components
import { BaseSidebarContainersItemComponent } from '@modules/common/components/base-sidebar-containers-item/base-sidebar-containers-item.component';

@Component({
  selector: 'app-side-bar-folders',
  templateUrl: './side-bar-folders.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-containers-item/base-sidebar-containers-item.component.less',
    './side-bar-folders.component.less',
  ],
  animations: [heightAnimation],
  standalone: false,
  providers: [MessageFolderListStateService.providers()],
})
export class SideBarFoldersComponent
  extends BaseSidebarContainersItemComponent<MessageFolder, Message, MessageFoldersFilters, VirtualFolder>
  implements OnInit
{
  @Input() messagesCount: MessagesCounts;
  @Input() mailboxTenantId: string | null | undefined;
  @Input() title = 'Folders';
  @Input() depth?: number;
  @Input() expandable = true;
  /** The Team Folders header draws the shared rail menu, as the team's. */
  @Input() teamRail = false;
  /** My Workspace's Folders header inside a team draws the shared rail menu too; a personal account's keeps its own. */
  @Input() memberRail = false;

  public application = Application.mail;
  public sidebarSplitViewKey = SidebarSplitViewKey.mail;
  public listStateKey = StateKey.sidebarMessagesFoldersListState;

  private readonly messageFolderListStateService = inject(MessageFolderListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /** The mailbox this rail's quick form creates in (its `[mailboxTenantId]`), for "Add details" too. */
  protected override railRequestContext(): HttpContext | undefined {
    return this.mailboxTenantId === undefined ? undefined : mailboxTenantContext(this.mailboxTenantId);
  }

  public ngOnInit(): void {
    super.ngOnInit();

    combineLatest([this.messageFolderListStateService.value(), this.displayTimeZoneService.resolvedTimeZone$])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        //
        // This menu draws no date control, so the zone buys nothing here TODAY — and waiting for it
        // costs something: `resolvedTimeZone$` holds until the account settings have been consulted,
        // and the tree below only fetches once `filters` is set, so the folders list asks for its
        // first page a beat later than it used to. Taken deliberately, and the same beat the files
        // and notes sidebars already wait. What it buys is the removal of a browser-zone bug that
        // was held off only by three separate facts about this menu staying true — and a missing
        // zone degrades silently rather than throwing, so nothing would have reported it.
        this.filters = new MessageFoldersFilters({ displayTimeZone }).applyListState(state);
      });
  }
}
